import { html, currentRoute, navigate } from 'onefold';
import { navSections } from '../config/navigation';

/**
 * Reactive breadcrumbs derived from the current route and navigation config.
 * Renders: Home > Section > Page
 */
export function Breadcrumbs(): Node {
  // Build a lookup: path → { section title, page label }
  const pathMap = new Map<string, { section: string; label: string }>();
  for (const section of navSections) {
    for (const link of section.links) {
      pathMap.set(link.path, { section: section.title, label: link.label });
    }
  }

  // Map section titles to their first link (section landing)
  const sectionFirstPath = new Map<string, string>();
  for (const section of navSections) {
    if (section.links.length > 0) {
      sectionFirstPath.set(section.title, section.links[0].path);
    }
  }

  const handleClick = (path: string) => (e: Event) => {
    e.preventDefault();
    navigate(path);
  };

  return html`
    <nav class="breadcrumbs" aria-label="Breadcrumb">
      ${() => {
        const route = currentRoute();
        const info = pathMap.get(route);

        // Don't show breadcrumbs on home page
        if (route === '/') return document.createTextNode('');

        const crumbs: Node[] = [];

        // Home crumb
        crumbs.push(html`<a class="breadcrumb-link" href="/" onclick=${handleClick('/')}>Home</a>`);
        crumbs.push(html`<span class="breadcrumb-sep" aria-hidden="true">/</span>`);

        if (info) {
          // Section crumb (links to first page in that section)
          const sectionPath = sectionFirstPath.get(info.section) ?? '/';
          if (sectionPath !== route) {
            crumbs.push(html`<a class="breadcrumb-link" href="${sectionPath}" onclick=${handleClick(sectionPath)}>${info.section}</a>`);
            crumbs.push(html`<span class="breadcrumb-sep" aria-hidden="true">/</span>`);
          }
          // Current page (not a link)
          crumbs.push(html`<span class="breadcrumb-current" aria-current="page">${info.label}</span>`);
        } else {
          // Unknown route — show path segments as-is
          const segments = route.split('/').filter(Boolean);
          const label = segments[segments.length - 1] ?? 'Page';
          crumbs.push(html`<span class="breadcrumb-current" aria-current="page">${label}</span>`);
        }

        const container = document.createDocumentFragment();
        for (const crumb of crumbs) container.appendChild(crumb);
        return container;
      }}
    </nav>
  `;
}
